import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
  Divider,
} from '@mui/material';
import {
  Note as NoteIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatDate } from '../utils';
import useAxios from '../hooks/useAxios';
import { useAppState } from '../state/context/AppStateContext';

interface IPendingLeaveRequest {
  id: string;
  employee_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  leave_from: string;
  leave_to: string;
  reason: string;
  is_half_day: boolean;
  created_at: string;
}

interface IPendingLeaveResponse {
  count: number;
  requests: IPendingLeaveRequest[];
}

const STORAGE_KEY = 'notificationIconPosition';

const NotificationIcon: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingLeaves, setPendingLeaves] = useState<IPendingLeaveResponse>({
    count: 0,
    requests: [],
  });
  const [open, setOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const getDefaultPosition = () => {
    const iconSize = 64;
    const spacing = 24;
    if (typeof window !== 'undefined') {
      return {
        left: window.innerWidth - iconSize - spacing,
        bottom: spacing,
      };
    }
    return {
      left: 0,
      bottom: spacing,
    };
  };

  const [position, setPosition] = useState(() => {
    // Try to load from localStorage first, otherwise use default
    if (typeof window !== 'undefined') {
      const savedPosition = localStorage.getItem(STORAGE_KEY);
      if (savedPosition) {
        try {
          return JSON.parse(savedPosition);
        } catch (error) {
          console.error('Error parsing saved position:', error);
        }
      }
    }
    return getDefaultPosition();
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const { fetch: getPendingLeaves } = useAxios<{ data: IPendingLeaveResponse }>();
  const { authStatus } = useAppState();

  const fetchPendingLeaves = async () => {
    try {
      const response = await getPendingLeaves({
        url: '/leave-requests/pending',
        method: 'GET',
      });
      if (response && response.status && response.data) {
        setPendingLeaves(response.data);
      }
    } catch (error) {
      console.error('Error fetching pending leaves:', error);
    }
  };

  useEffect(() => {
    fetchPendingLeaves();
  }, []);

  useEffect(() => {
    // Update position on window resize to maintain bottom-right if no saved position
    const handleResize = () => {
      const savedPosition = localStorage.getItem(STORAGE_KEY);
      if (!savedPosition) {
        // Only update if no saved position exists
        setPosition(getDefaultPosition());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Save position to localStorage whenever it changes
    if (!isDragging) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }
  }, [position, isDragging]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setHasMoved(true);
      const iconSize = 64;
      const newLeft = e.clientX - dragStart.x;
      const newBottom = window.innerHeight - (e.clientY - dragStart.y) - iconSize;

      // Constrain to viewport
      const maxLeft = window.innerWidth - iconSize;
      const maxBottom = window.innerHeight - iconSize;

      setPosition({
        left: Math.max(0, Math.min(newLeft, maxLeft)),
        bottom: Math.max(0, Math.min(newBottom, maxBottom)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Reset hasMoved after a short delay to allow click handler to check it
      setTimeout(() => setHasMoved(false), 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    // Don't start drag if clicking on close button
    if ((e.target as HTMLElement).closest('[title="Hide notifications"]')) {
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
    setHasMoved(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Don't open popover if we just finished dragging
    if (hasMoved) {
      return;
    }
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
    // Refresh when opening
    fetchPendingLeaves();
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleHideIcon = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsHidden(true);
  };

  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsRefreshing(true);
    try {
      await fetchPendingLeaves();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Don't show if user is not logged in, if there are no pending leaves, or if hidden
  if (!authStatus || pendingLeaves.count === 0 || isHidden) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        sx={{
          position: 'fixed',
          left: position.left,
          bottom: position.bottom,
          zIndex: 1300,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
          }}
        >
          <IconButton
            onClick={handleClick}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              width: 64,
              height: 64,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              boxShadow: 3,
              cursor: 'pointer',
            }}
          >
            <Badge
              badgeContent={pendingLeaves.count}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  right: 6,
                  top: 6,
                  fontSize: '0.75rem',
                  minWidth: 20,
                  height: 20,
                },
              }}
            >
              <NoteIcon sx={{ fontSize: 36 }} />
            </Badge>
          </IconButton>
          <IconButton
            onClick={handleHideIcon}
            size="small"
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(4px)',
              color: 'white',
              width: 20,
              height: 20,
              minWidth: 20,
              padding: 0,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
              boxShadow: 2,
              zIndex: 1,
            }}
            title="Hide notifications"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="top-end"
          sx={{ zIndex: 1301 }}
        >
          <Paper
            sx={{
              width: 400,
              maxHeight: 500,
              overflow: 'auto',
              mt: 1,
              boxShadow: 3,
            }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: 'primary.main',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                Pending Leave Requests ({pendingLeaves.count})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  onClick={handleRefresh}
                  size="small"
                  disabled={isRefreshing}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:disabled': {
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                  title="Refresh"
                >
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  title="Close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <List>
              {pendingLeaves.requests.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No pending leave requests" />
                </ListItem>
              ) : (
                pendingLeaves.requests.map((request, index) => (
                  <React.Fragment key={request.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight="bold">
                            {request.first_name} {request.last_name}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(request.leave_from)} - {formatDate(request.leave_to)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {request.is_half_day ? 'Half Day' : 'Full Day'}
                            </Typography>
                            {request.reason && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Reason: {request.reason}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < pendingLeaves.requests.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              )}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default NotificationIcon;

